import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { registerUser, clearError } from '../../../store/slices/authSlice';
import { SignupCredentials } from '../../../types/portal';
import MultiSelect from '../../common/MultiSelect';

const Form = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const InputGroup = styled.div<{ fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  grid-column: ${props => props.fullWidth ? '1 / -1' : 'auto'};

  label {
    font-size: 0.85rem;
    font-weight: 700;
    color: #444;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  input, select {
    padding: 0.875rem 1rem;
    border-radius: 12px;
    border: 2px solid #eee;
    font-size: 1rem;
    transition: all 0.2s;
    background: #f9f9f9;

    &:focus {
      outline: none;
      border-color: var(--primary-orange);
      background: #fff;
    }
  }
`;

const SectionTitle = styled.h3`
  grid-column: 1 / -1;
  font-size: 0.9rem;
  font-weight: 800;
  text-transform: uppercase;
  color: #111;
  margin: 1rem 0 0.5rem 0;
  letter-spacing: 0.05em;
`;

const FormTitle = styled.h2`
  grid-column: 1 / -1;
  font-size: 1.5rem;
  font-weight: 800;
  color: #111;
  margin: 0 0 1.5rem 0;
  letter-spacing: -0.02em;

  @media (max-width: 600px) {
    margin-bottom: 0.75rem;
    font-size: 1.25rem;
  }
`;

const ErrorBanner = styled.div`
  grid-column: 1 / -1;
  background: #fef2f2;
  border: 1px solid #fee2e2;
  color: #ef4444;
  padding: 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;
  text-align: center;
`;

const SuccessBanner = styled.div`
  grid-column: 1 / -1;
  background: #f0fdf4;
  border: 1px solid #dcfce7;
  color: #16a34a;
  padding: 1.5rem;
  border-radius: 12px;
  text-align: center;
  font-weight: 600;
`;

const SubmitButton = styled.button`
  grid-column: 1 / -1;
  background: var(--primary-orange);
  color: #fff;
  border: none;
  border-radius: 12px;
  padding: 1rem;
  font-size: 1.1rem;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.3s;
  margin-top: 1rem;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(var(--primary-orange-rgb, 236, 92, 44), 0.2);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const CaptchaWrapper = styled.div`
  grid-column: 1 / -1;
  display: flex;
  justify-content: center;
  margin: 1rem 0;
`;

const SubText = styled.p`
  grid-column: 1 / -1;
  text-align: center;
  font-size: 0.85rem;
  color: #666;
  margin-top: 0.5rem;
`;

interface RegisterFormProps {
  onSuccess?: () => void;
  onLogin?: () => void;
  onSwitchRole?: () => void;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onSuccess, onLogin, onSwitchRole }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { isLoading, error } = useAppSelector((state) => state.auth);
  const [success, setSuccess] = useState(false);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const [credentials, setCredentials] = useState<SignupCredentials>({
    houseNumber: '',
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    drivingSchoolName: '',
    postcode: '',
    roles: ['ROLE_TEACHER'],
    teacherLicenses: [],
    recaptchaToken: ''
  });

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setCredentials((prev: SignupCredentials) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLicenseChange = (selected: string[]) => {
    setCredentials(prev => ({ ...prev, teacherLicenses: selected }));
  };

  const onCaptchaChange = (token: string | null) => {
    setCredentials(prev => ({ ...prev, recaptchaToken: token || '' }));
    // If we have a token and we just showed the captcha, we could auto-submit here 
    // but it's safer to let the user click the button which is now enabled.
  };

  const handleRegisterClick = (e: React.MouseEvent) => {
    // Basic validation check before showing captcha
    const form = (e.target as HTMLButtonElement).form;
    if (form && form.checkValidity()) {
      setShowCaptcha(true);
    } else if (form) {
      form.reportValidity();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!credentials.recaptchaToken) {
      // In a real scenario, you might want to show a specific error if captcha is missing
      // but usually the button is disabled or the backend will catch it.
      // We'll let the backend catch it for now if it reaches here.
    }

    const resultAction = await dispatch(registerUser(credentials));
    if (registerUser.fulfilled.match(resultAction)) {
      setSuccess(true);
      if (onSuccess) {
        setTimeout(onSuccess, 3000);
      }
    } else {
      // Reset captcha on failure
      recaptchaRef.current?.reset();
      setCredentials(prev => ({ ...prev, recaptchaToken: '' }));
    }
  };

  const licenseOptions = [
    { value: 'AM', label: t('portal.register.license_am', 'Bromfiets (AM)') },
    { value: 'A1', label: t('portal.register.license_a1', 'Motor lichte (A1)') },
    { value: 'A2', label: t('portal.register.license_a2', 'Motor middelzware (A2)') },
    { value: 'A', label: t('portal.register.license_a', 'Motor zware (A)') },
    { value: 'B_MANUAL', label: t('portal.register.license_b_manual', 'Auto (B) - Handgeschakeld') },
    { value: 'B_AUTOMATIC', label: t('portal.register.license_b_automatic', 'Auto (B) - Automaat') },
    { value: 'BE', label: t('portal.register.license_be', 'Auto met aanhangwagen (BE)') },
    { value: 'B_PLUS', label: t('portal.register.license_b_plus', 'Auto met zware aanhangwagen (B+)') },
    { value: 'C1', label: t('portal.register.license_c1', 'Vrachtauto lichte (C1)') },
    { value: 'C1E', label: t('portal.register.license_c1e', 'Vrachtauto lichte met aanhangwagen (C1E)') },
    { value: 'C', label: t('portal.register.license_c', 'Vrachtauto (C)') },
    { value: 'CE', label: t('portal.register.license_ce', 'Vrachtauto met aanhangwagen (CE)') },
    { value: 'D1', label: t('portal.register.license_d1', 'Bus lichte (D1)') },
    { value: 'D1E', label: t('portal.register.license_d1e', 'Bus lichte met aanhangwagen (D1E)') },
    { value: 'D', label: t('portal.register.license_d', 'Bus (D)') },
    { value: 'DE', label: t('portal.register.license_de', 'Bus met aanhangwagen (DE)') },
    { value: 'T', label: t('portal.register.license_t', 'Tractor (T)') },
  ];

  if (success) {
    return (
      <SuccessBanner>
        {t('portal.register.success', 'Registration successful! You can now log in with your email address.')}
      </SuccessBanner>
    );
  }

  return (
    <Form onSubmit={handleSubmit}>
      <FormTitle>{t('portal.register.teacher_title', 'Registreer als instructeur')}</FormTitle>
      {error && <ErrorBanner>{error}</ErrorBanner>}

      <InputGroup fullWidth>
        <input
          type="text"
          id="drivingSchoolName"
          name="drivingSchoolName"
          value={credentials.drivingSchoolName}
          onChange={handleChange}
          placeholder={t('portal.register.driving_school_name', 'Naam rijschool') as string}
          required
          disabled={isLoading}
        />
      </InputGroup>

      <InputGroup>
        <input
          type="text"
          id="postcode"
          name="postcode"
          value={credentials.postcode}
          onChange={handleChange}
          placeholder={t('portal.register.postcode', 'Postcode') as string}
          required
          disabled={isLoading}
        />
      </InputGroup>

      <InputGroup>
        <input
          type="text"
          id="houseNumber"
          name="houseNumber"
          value={credentials.houseNumber}
          onChange={handleChange}
          placeholder={t('portal.register.house_number', 'Huisnummer') as string}
          required
          disabled={isLoading}
        />
      </InputGroup>

      <InputGroup fullWidth>
        <MultiSelect
          options={licenseOptions}
          selectedValues={credentials.teacherLicenses || []}
          onChange={handleLicenseChange}
          placeholder={t('portal.register.select_license', 'Selecteer type rijbewijs') as string}
        />
      </InputGroup>


      <InputGroup>
        <input
          type="text"
          id="firstName"
          name="firstName"
          value={credentials.firstName}
          onChange={handleChange}
          placeholder={t('portal.register.first_name', 'Voornaam') as string}
          required
          disabled={isLoading}
        />
      </InputGroup>

      <InputGroup>
        <input
          type="text"
          id="lastName"
          name="lastName"
          value={credentials.lastName}
          onChange={handleChange}
          placeholder={t('portal.register.last_name', 'Achternaam') as string}
          required
          disabled={isLoading}
        />
      </InputGroup>

      <InputGroup fullWidth>
        <input
          type="email"
          id="email"
          name="email"
          value={credentials.email}
          onChange={handleChange}
          placeholder={t('portal.register.email', 'Email') as string}
          required
          disabled={isLoading}
        />
      </InputGroup>

      <InputGroup fullWidth>
        <input
          type="password"
          id="password"
          name="password"
          value={credentials.password}
          onChange={handleChange}
          placeholder={t('portal.register.password', 'Wachtwoord') as string}
          required
          disabled={isLoading}
          minLength={8}
        />
      </InputGroup>

      {showCaptcha && (
        <CaptchaWrapper>
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'}
            onChange={onCaptchaChange}
          />
        </CaptchaWrapper>
      )}

      {!showCaptcha ? (
        <SubmitButton type="button" onClick={handleRegisterClick} disabled={isLoading}>
          {t('portal.register.submit', 'Aanmelden')}
        </SubmitButton>
      ) : (
        <SubmitButton type="submit" disabled={isLoading || !credentials.recaptchaToken}>
          {isLoading ? t('portal.register.loading', 'Creating account...') : t('portal.register.submit', 'Aanmelden')}
        </SubmitButton>
      )}

      <SubText>
        {onLogin && (
          <div style={{ marginBottom: '0.5rem' }}>
            {t('portal.register.have_account', 'Heb je al een account?')}{' '}
            <button 
              type="button" 
              onClick={onLogin}
              style={{ 
                background: 'none', 
                border: 'none', 
                color: 'var(--primary-orange)', 
                fontWeight: 700, 
                cursor: 'pointer',
                padding: 0,
                fontSize: 'inherit'
              }}
            >
              {t('portal.login.submit', 'Inloggen')}
            </button>
          </div>
        )}
        {onSwitchRole && (
          <div>
            <button 
              type="button" 
              onClick={onSwitchRole}
              style={{ 
                background: 'none', 
                border: 'none', 
                color: 'var(--primary-orange)', 
                fontWeight: 600, 
                cursor: 'pointer',
                padding: 0,
                fontSize: '0.85rem'
              }}
            >
              {t('portal.register.switch_to_student', 'Ben je een leerling? Registreer hier')}
            </button>
          </div>
        )}
        {!onLogin && !onSwitchRole && t('portal.register.no_commitment', 'Aanmelden is vrijblijvend. Je zit nergens aan vast.')}
      </SubText>
    </Form>
  );
};

export default RegisterForm;
